'use client';

// Chrome/Firefox: "not a child of this node". jsdom: "can not be found in the parent".
const DOM_SYNC_MESSAGE =
  /not a child of this node|can not be found in the parent/i;
const PATCHED = Symbol.for('ollie.domSyncProbe');
const MAX_CLASS_LENGTH = 120;
const MAX_PATH_DEPTH = 8;

let domSyncTriggered = false;
let domSyncReported = false;
let domSyncPhase = 'idle';
let lastDomSyncContext = null;
let stashedError = null;
let stashedComponentStack = null;

export const isDomSyncError = error =>
  error?.name === 'NotFoundError' &&
  DOM_SYNC_MESSAGE.test(error?.message ?? '');

export const hasDomSyncTriggered = () => domSyncTriggered;

export const markDomSyncTriggered = () => {
  domSyncTriggered = true;
};

export const isDomSyncRetrying = () => domSyncPhase === 'retrying';

export const hasDomSyncRetried = () => domSyncPhase === 'retried';

export const beginDomSyncRetry = () => {
  domSyncPhase = 'retrying';
};

export const failDomSyncRetry = () => {
  domSyncPhase = 'failed';
  domSyncTriggered = true;
};

// A successful remount stays exhausted for this document. The next DOM-sync
// error is a second miss, so the page gets one retry per load.
export const noteDomSyncRetrySucceeded = () => {
  if (domSyncPhase === 'retrying') domSyncPhase = 'retried';
};

export const hasDomSyncBeenReported = () => domSyncReported;

export const markDomSyncReported = () => {
  domSyncReported = true;
};

export const getLastDomSyncContext = () => lastDomSyncContext;

export const stashDomSyncError = error => {
  stashedError = error;
};

export const stashDomSyncComponentStack = componentStack => {
  stashedComponentStack = componentStack;
};

export const getStashedDomSyncError = () => stashedError;

export const getStashedComponentStack = () => stashedComponentStack;

export const resetDomSyncStateForTests = () => {
  domSyncTriggered = false;
  domSyncReported = false;
  domSyncPhase = 'idle';
  lastDomSyncContext = null;
  stashedError = null;
  stashedComponentStack = null;
};

const classNameOf = element => {
  const value = element?.getAttribute?.('class');
  if (!value) return undefined;
  return value.length > MAX_CLASS_LENGTH
    ? `${value.slice(0, MAX_CLASS_LENGTH)}…`
    : value;
};

const elementPath = node => {
  const parts = [];
  let current = node?.nodeType === 1 ? node : node?.parentElement;
  let depth = 0;

  while (current && depth < MAX_PATH_DEPTH) {
    let part = current.nodeName.toLowerCase();
    if (current.id) {
      part += `#${current.id}`;
    } else {
      const className = current.getAttribute?.('class')?.split(/\s+/)[0];
      if (className) part += `.${className}`;
    }
    parts.unshift(part);

    const root = current.getRootNode?.();
    if (typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) {
      parts.unshift('shadow');
      current = root.host;
    } else {
      current = current.parentElement;
    }
    depth += 1;
  }

  return parts.join(' > ');
};

const describeNode = node => {
  if (!node || typeof node.nodeName !== 'string') return null;

  const element = node.nodeType === 1 ? node : node.parentElement;
  const root = node.getRootNode?.();
  const shadowHost =
    typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot
      ? root.host?.nodeName?.toLowerCase()
      : undefined;

  return {
    nodeName: node.nodeName,
    id: element?.id || undefined,
    className: classNameOf(element),
    shadowHost,
    path: elementPath(node),
  };
};

const rememberDomSyncFailure = (method, parent, args) => {
  const child = args[0];
  const reference = method === 'insertBefore' ? args[1] : undefined;

  lastDomSyncContext = {
    method,
    url: window.location?.href,
    parent: describeNode(parent),
    child: describeNode(child),
    childParent: describeNode(child?.parentNode),
    reference: describeNode(reference),
    referenceParent: describeNode(reference?.parentNode),
  };
};

export const installDomSyncProbe = () => {
  if (typeof window === 'undefined' || typeof Node !== 'function') return;

  ['insertBefore', 'removeChild'].forEach(method => {
    const original = Node.prototype[method];
    if (!original || original[PATCHED]) return;

    function probedDomMethod(...args) {
      try {
        return original.apply(this, args);
      } catch (error) {
        if (isDomSyncError(error)) {
          rememberDomSyncFailure(method, this, args);
          // The exception React reports does not include the nodes. Hang the
          // snapshot here so Sentry can copy it onto the event.
          error.domSync = lastDomSyncContext;
        }
        throw error;
      }
    }

    probedDomMethod[PATCHED] = true;
    Node.prototype[method] = probedDomMethod;
  });
};
